import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAppThunkDispatch } from 'state/store';
import {
  addUserToLdTeam,
  removeUserFromLdTeam,
  setUpdateLDTeamMembersNotifications,
  selectLearningTeamMembers,
} from 'state/LearningTeam/learningTeamSlice';
import { LearningTeamWithMembers } from 'utils/types/learningTeam';

export const useTeamMembersManagement = () => {
  const dispatch = useAppThunkDispatch();
  const { teamId } = useParams<{
    teamId: string;
  }>();
  const learningTeamMembers = useSelector(selectLearningTeamMembers);

  const parseAddUsersResponse = useCallback(
    (
      usersIds: string[],
      response: PromiseSettledResult<LearningTeamWithMembers>[]
    ) => {
      const succeededIds: string[] = [];
      const failedIds: string[] = [];
      usersIds.forEach((userId, index) => {
        if (response[index].status === 'fulfilled') {
          succeededIds.push(userId);
        } else {
          failedIds.push(userId);
        }
      });
      return {
        succeededIds,
        failedIds,
      };
    },
    []
  );

  const notifyAddMembersResult = useCallback(
    (successCount: number, errorCount: number) => {
      dispatch(
        setUpdateLDTeamMembersNotifications({
          successCount,
          errorCount,
        })
      );
    },
    [dispatch]
  );

  const addTeamMembers = useCallback(
    async (usersIds: string[]) => {
      try {
        const results = await Promise.allSettled(
          usersIds.map((userId) =>
            dispatch(
              addUserToLdTeam({
                userId,
                teamId: teamId as string,
              })
            ).unwrap()
          )
        );
        const { succeededIds, failedIds } = parseAddUsersResponse(
          usersIds,
          results
        );
        notifyAddMembersResult(succeededIds.length, failedIds.length);
        return { succeededIds, failedIds };
      } catch {
        return { succeededIds: [], failedIds: usersIds };
      }
    },
    [dispatch, parseAddUsersResponse, teamId, notifyAddMembersResult]
  );

  const removeTeamMember = useCallback(
    async (userId: string) => {
      return await dispatch(
        removeUserFromLdTeam({ userId, teamId: teamId as string })
      );
    },
    [dispatch, teamId]
  );

  return {
    addTeamMembers,
    removeTeamMember,
    learningTeamMembers,
  };
};
